import { useEffect, useRef, useState } from "react";
import { openQuoteStream, openTickerStream, validProducts } from "@/lib/liveTransport";
import { STALE_MS, assessStreamed } from "@/lib/freshness";

const MAX_POINTS = 300; // 5 minutes of 1-second samples
const MAX_CANDLES = 120;
const FLUSH_MS = 200; // real-time UI refresh cadence (5 fps)

const EMPTY = { status: "idle", source: null, price: null, bid: null, ask: null, open24h: null, series: [], candles: [], ticks: 0, lastTickAt: null };

// Streams real-time trades for a coin symbol (e.g. "btc"). Every tick updates the current
// candle (O/H/L/C/volume) and the UI is flushed ~5×/s; a 1-second sampler feeds the line series.
// Transport: direct exchange socket, automatic backend-relay fallback.
export function useLiveTicker(symbol, enabled = true, intervalSec = 1) {
  const [state, setState] = useState(EMPTY);
  const sym = (symbol || "").toUpperCase();
  const intervalRef = useRef(intervalSec);
  const ctl = useRef(null);

  // Changing the candle interval rebuilds candles from the tick buffer — no reconnect.
  useEffect(() => {
    intervalRef.current = intervalSec;
    ctl.current?.rebuild();
  }, [intervalSec]);

  useEffect(() => {
    if (!enabled || !sym) { setState(EMPTY); return undefined; }
    let dirty = false, source = null, feedConnected = false, feedAt = null;
    const latest = { price: null, bid: null, ask: null, open24h: null, ticks: 0, lastTickAt: null };
    let series = [], candles = [], ticksBuf = [];
    const start = Date.now();
    setState({ ...EMPTY, status: "connecting" });

    const bucket = (t) => Math.floor(t / (intervalRef.current * 1000)) * intervalRef.current * 1000;

    const addToCandles = (t, price, size) => {
      const b = bucket(t);
      const cur = candles[candles.length - 1];
      if (cur && cur.t === b) {
        cur.h = Math.max(cur.h, price); cur.l = Math.min(cur.l, price); cur.c = price; cur.v += size; cur.n += 1;
      } else {
        if (cur) cur.live = false;
        candles = [...candles, { t: b, o: cur ? cur.c : price, h: price, l: price, c: price, v: size, n: 1, live: true }].slice(-MAX_CANDLES);
      }
    };

    ctl.current = { rebuild: () => { candles = []; ticksBuf.forEach((x) => addToCandles(x.t, x.p, x.s)); dirty = true; } };

    let handle = { close() {} };
    const onTick = (_s, price, extra = {}) => {
      if (!Number.isFinite(price)) return;
      const now = Date.now();
      Object.assign(latest, extra, { price, ticks: latest.ticks + 1, lastTickAt: now });
      const size = Number.isFinite(extra.size) ? extra.size : 0;
      ticksBuf.push({ t: now, p: price, s: size });
      if (ticksBuf.length > 6000) ticksBuf = ticksBuf.slice(-5000);
      addToCandles(now, price, size);
      dirty = true;
    };
    const onStatus = (s) => {
      source = s;
      if (s === "unavailable") setState((st) => ({ ...st, status: "unavailable", source: s }));
    };
    const onHealth = (h) => { feedConnected = h.connected; feedAt = h.connected ? Date.now() : feedAt; dirty = true; };
    let cancelled = false;
    // Coinbase-listed coins: direct exchange socket (sub-second, relay fallback). Everything else: aggregated feed (Kraken / KuCoin / Coinbase) via SSE.
    validProducts().then((v) => {
      if (cancelled) return;
      handle = v && v.has(`${sym}-USD`) ? openTickerStream([`${sym}-USD`], onTick, onStatus, onHealth) : openQuoteStream(sym, onTick, onStatus, onHealth);
    });

    const flush = () => {
      if (!dirty || latest.price == null) return;
      dirty = false;
      const fresh = assessStreamed({ at: latest.lastTickAt, feedUp: !!(feedConnected && feedAt && Date.now() - feedAt < STALE_MS), srvNow: Date.now() });
      setState({
        status: fresh.state, feed: fresh.feed, source, price: latest.price, bid: latest.bid, ask: latest.ask, open24h: latest.open24h,
        series, candles: candles.map((c) => ({ ...c })), ticks: latest.ticks, lastTickAt: latest.lastTickAt,
      });
    };

    const flusher = setInterval(flush, FLUSH_MS);
    const sampler = setInterval(() => {
      if (latest.price == null) {
        if (Date.now() - start > 15000 && latest.ticks === 0) setState((s) => (s.status === "connecting" ? { ...s, status: "unavailable" } : s));
        return;
      }
      const prev = series[series.length - 1];
      series = [...series, { t: Date.now(), p: latest.price, up: prev ? latest.price >= prev.p : true }].slice(-MAX_POINTS);
      // Roll an empty candle forward when no trade happened this interval so the clock keeps moving
      const b = bucket(Date.now());
      const cur = candles[candles.length - 1];
      if (cur && cur.t !== b) { cur.live = false; candles = [...candles, { t: b, o: cur.c, h: cur.c, l: cur.c, c: cur.c, v: 0, n: 0, live: true }].slice(-MAX_CANDLES); }
      dirty = true;
    }, 1000);

    return () => {
      ctl.current = null; cancelled = true;
      clearInterval(flusher); clearInterval(sampler);
      handle.close();
    };
  }, [sym, enabled]);

  return state;
}
